import { AppUrlSearchParams } from "@/functions/appUrlSearchParams";
import { pinia } from "@/stores";

// FontAwesome
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCheckSquare, faSquare } from "@fortawesome/free-regular-svg-icons";
import {
  faAnchor,
  faBackward,
  faCalendar,
  faCalendarTimes,
  faChartArea,
  faCheckCircle,
  faCodeBranch,
  faColumns,
  faCopy,
  faEdit,
  faEnvelope,
  faExclamationTriangle,
  faFileAlt,
  faFileContract,
  faFileCsv,
  faFilePdf,
  faFileSignature,
  faFilter,
  faFingerprint,
  faHammer,
  faIndustry,
  faInfoCircle,
  faKey,
  faLaptopHouse,
  faLink,
  faList,
  faListAlt,
  faLock,
  faMobile,
  faPaperPlane,
  faPen,
  faPercentage,
  faPlus,
  faPoo,
  faSave,
  faShip,
  faSignInAlt,
  faSignOutAlt,
  faStickyNote,
  faStopwatch,
  faSync,
  faTable,
  faTasks,
  faTimes,
  faTools,
  faTrash,
  faTrashRestore,
  faTruck,
  faUnlock,
  faUser,
  faUserCheck,
  faUserCog,
  faUserEdit,
  faUserLock,
  faUserMinus,
  faUserPlus,
  faUsers,
  faUserShield,
  faUserTag,
  faVial,
  faWindowClose,
} from "@fortawesome/free-solid-svg-icons";
import { faChartBar } from "@fortawesome/free-solid-svg-icons/faChartBar";
import { faChartPie } from "@fortawesome/free-solid-svg-icons/faChartPie";
import { faDog } from "@fortawesome/free-solid-svg-icons/faDog";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { definePreset } from "@primeuix/themes";
import Aura from "@primeuix/themes/aura";
import { equals } from "@primeuix/utils/object";
import { FilterService } from "@primevue/core/api";
//HighCharts
import HighchartsVue from "highcharts-vue";
import BlockUI from "primevue/blockui";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import Column from "primevue/column";
import ColumnGroup from "primevue/columngroup";

//PrimeVue
import PrimeVue from "primevue/config";
import ConfirmationService from "primevue/confirmationservice";
import ConfirmPopup from "primevue/confirmpopup";
import DataTable from "primevue/datatable";
import DatePicker from "primevue/datepicker";
import Dialog from "primevue/dialog";
import DialogService from "primevue/dialogservice";

// PrimeVue CSS and other css
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "./stylesheets/main.scss";
import "vue-json-pretty/lib/styles.css";
import Dropdown from "primevue/dropdown";
import DynamicDialog from "primevue/dynamicdialog";
import IconField from "primevue/iconfield";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import InputIcon from "primevue/inputicon";
import InputNumber from "primevue/inputnumber";
import InputText from "primevue/inputtext";
import Menu from "primevue/menu";
import Message from "primevue/message";
import MultiSelect from "primevue/multiselect";
import Panel from "primevue/panel";
import Password from "primevue/password";
import ProgressBar from "primevue/progressbar";
import ProgressSpinner from "primevue/progressspinner";
import RadioButton from "primevue/radiobutton";
import TabMenu from "primevue/tabmenu";
import Toast from "primevue/toast";
import ToastService from "primevue/toastservice";
import ToggleButton from "primevue/togglebutton";
import Tooltip from "primevue/tooltip";
import { createApp } from "vue";
import VueJsonPretty from "vue-json-pretty";
import App from "./App.vue";
import router from "./router";
import store from "./store";

library.add(
  faSquare,
  faCheckSquare,
  faUser,
  faUserLock,
  faUserCheck,
  faUserCog,
  faUserMinus,
  faUserPlus,
  faUserEdit,
  faUserTag,
  faUserShield,
  faList,
  faShip,
  faCalendarTimes,
  faEnvelope,
  faLock,
  faSignInAlt,
  faSignOutAlt,
  faTimes,
  faLaptopHouse,
  faSync,
  faKey,
  faFileSignature,
  faStopwatch,
  faDog,
  faChartBar,
  faChartPie,
  faChartArea,
  faFingerprint,
  faCodeBranch,
  faAnchor,
  faEdit,
  faInfoCircle,
  faSave,
  faWindowClose,
  faCheckCircle,
  faCopy,
  faPlus,
  faTrash,
  faTrashRestore,
  faLink,
  faPen,
  faFilter,
  faUnlock,
  faIndustry,
  faUsers,
  faTable,
  faColumns,
  faBackward,
  faPercentage,
  faVial,
  faTruck,
  faTools,
  faTasks,
  faFileContract,
  faPoo,
  faHammer,
  faListAlt,
  faPaperPlane,
  faMobile,
  faStickyNote,
  faFileAlt,
  faCalendar,
  faFilePdf,
  faFileCsv,
  faExclamationTriangle
);

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: "{blue.50}",
      100: "{blue.100}",
      200: "{blue.200}",
      300: "{blue.300}",
      400: "{blue.400}",
      500: "{blue.500}",
      600: "{blue.600}",
      700: "{blue.700}",
      800: "{blue.800}",
      900: "{blue.900}",
      950: "{blue.950}",
    },
    colorScheme: {
      light: {
        surface: {
          0: "#ffffff",
          50: "{zinc.50}",
          100: "{zinc.100}",
          200: "{zinc.200}",
          300: "{zinc.300}",
          400: "{zinc.400}",
          500: "{zinc.500}",
          600: "{zinc.600}",
          700: "{zinc.700}",
          800: "{zinc.800}",
          900: "{zinc.900}",
          950: "{zinc.950}",
        },
      },
      dark: {
        surface: {
          0: "#ffffff",
          50: "{slate.50}",
          100: "{slate.100}",
          200: "{slate.200}",
          300: "{slate.300}",
          400: "{slate.400}",
          500: "{slate.500}",
          600: "{slate.600}",
          700: "{slate.700}",
          800: "{slate.800}",
          900: "{slate.900}",
          950: "{slate.950}",
        },
      },
    },
  },
});

FilterService.register("inWithEmpty", (value, filter) => {
  if (filter == null || filter.length === 0) {
    return true;
  }
  for (let i = 0; i < filter.length; i++) {
    if ((filter[i] === null && value === "") || equals(value, filter[i])) {
      return true;
    }
  }
  return false;
});

FilterService.register("inArray", (value, filter) => {
  if (filter == null || filter.length === 0) {
    return true;
  }

  if (!value?.length) return false;

  for (let i = 0; i < filter.length; i++) {
    if (value.some((x) => equals(x, filter[i]))) {
      return true;
    }
  }
  return false;
});

FilterService.register("inArrayWithEmpty", (value, filter) => {
  if (filter == null || filter.length === 0) {
    return true;
  }
  for (let i = 0; i < filter.length; i++) {
    if (
      (filter[i] === null && (value == null || value.length === 0)) ||
      value.some((x) => equals(x, filter[i]))
    ) {
      return true;
    }
  }
  return false;
});

createApp(App)
  .use(store)
  .use(pinia)
  .use(router)
  .use(PrimeVue, {
    ripple: true,
    theme: {
      preset: MyPreset,
    },
  })
  .use(ToastService)
  .use(ConfirmationService)
  .use(DialogService)
  .use(HighchartsVue)
  .use(AppUrlSearchParams)
  .component("VueJsonPretty", VueJsonPretty)
  //   .use(VueCsrf, {
  //   selector: 'meta[name="csrf-token"]', // selector of csrf element with csrf-token value
  //   attribute: 'content', //attribute of csrf-token element
  // })
  .directive("tooltip", Tooltip)
  .component("InputGroup", InputGroup)
  .component("InputGroupAddon", InputGroupAddon)
  .component("InputText", InputText)
  .component("IconField", IconField)
  .component("InputIcon", InputIcon)
  .component("Message", Message)
  .component("InputNumber", InputNumber)
  .component("Password", Password)
  .component("Button", Button)
  .component("Checkbox", Checkbox)
  .component("RadioButton", RadioButton)
  .component("ProgressSpinner", ProgressSpinner)
  .component("ProgressBar", ProgressBar)
  .component("Toast", Toast)
  .component("TabMenu", TabMenu)
  .component("Menu", Menu)
  .component("Panel", Panel)
  .component("DataTable", DataTable)
  .component("Column", Column)
  .component("ColumnGroup", ColumnGroup)
  .component("ToggleButton", ToggleButton)
  .component("DatePicker", DatePicker)
  .component("Dropdown", Dropdown)
  .component("MultiSelect", MultiSelect)
  .component("BlockUI", BlockUI)
  .component("Dialog", Dialog)
  .component("ConfirmPopup", ConfirmPopup)
  .component("DynamicDialog", DynamicDialog)
  .component("font-awesome-icon", FontAwesomeIcon)
  .mount("#app");
